import { type PointerEvent, useEffect, useRef } from 'react';

const DISMISS_DISTANCE_RATIO = 0.25;
const MIN_DISMISS_DISTANCE = 80;
const DISMISS_VELOCITY = 0.5;
const SLIDE_MS = 200;
const SLIDE_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

type Drag = {
  sheet: HTMLElement;
  startY: number;
  lastY: number;
  lastTime: number;
  offset: number;
  velocity: number;
};

export function useSheetDragToDismiss(onDismiss: () => void) {
  const handleRef = useRef<HTMLSpanElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
      }
    },
    []
  );

  function handlePointerDown(event: PointerEvent<HTMLSpanElement>) {
    if (!event.isPrimary) {
      return;
    }

    const sheet = handleRef.current?.closest<HTMLElement>('[role="dialog"]');

    if (!sheet) {
      return;
    }

    event.currentTarget.setPointerCapture?.(event.pointerId);
    sheet.style.transition = 'none';

    dragRef.current = {
      sheet,
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      offset: 0,
      velocity: 0,
    };
  }

  function handlePointerMove(event: PointerEvent<HTMLSpanElement>) {
    const drag = dragRef.current;

    if (!drag) {
      return;
    }

    const elapsed = event.timeStamp - drag.lastTime;

    if (elapsed > 0) {
      drag.velocity = (event.clientY - drag.lastY) / elapsed;
    }

    drag.lastY = event.clientY;
    drag.lastTime = event.timeStamp;
    drag.offset = Math.max(0, event.clientY - drag.startY);
    drag.sheet.style.transform = `translateY(${drag.offset}px)`;
  }

  function handlePointerUp() {
    const drag = dragRef.current;

    if (!drag) {
      return;
    }

    dragRef.current = null;

    const { sheet, offset, velocity } = drag;
    const dismissDistance = Math.max(
      MIN_DISMISS_DISTANCE,
      sheet.offsetHeight * DISMISS_DISTANCE_RATIO
    );

    sheet.style.transition = `transform ${SLIDE_MS}ms ${SLIDE_EASING}`;

    if (offset >= dismissDistance || velocity >= DISMISS_VELOCITY) {
      sheet.style.transform = 'translateY(100%)';
      dismissTimeoutRef.current = setTimeout(onDismiss, SLIDE_MS);
      return;
    }

    sheet.style.transform = 'translateY(0)';
  }

  return {
    handleRef,
    dragHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
    },
  };
}
